import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { MeddraTerm } from '@/lib/api/generated/casedb';
import { CODING_ACTION, parseCoding } from '@/features/meddra/shared/model/meddraCoding';
import { codingDraftValues } from '../model/codingDraftPreview';
import { dictionaryTerm } from '../services/meddraApi';

interface CodingDraftPreview {
  values?: Record<string, string | null>;
  term?: MeddraTerm;
  loading: boolean;
  error: string;
}
export const CodingDraftPreviewContext = createContext<CodingDraftPreview | undefined>(undefined);
export const useCodingPreview = () => useContext(CodingDraftPreviewContext);

/** One lookup per record; never writes projected display values into the editable draft/PATCH. */
export function useCodingDraftPreview(value: unknown, fieldId: string): CodingDraftPreview {
  const parsed = useMemo(() => {
    try { return { state: parseCoding(value), error: '' }; }
    catch (error) { return { state: {}, error: String(error) }; }
  }, [value]);
  const state = parsed.state;
  const [resolved, setResolved] = useState<{ value: unknown; term?: MeddraTerm; error: string }>();
  const lookup = state.action === CODING_ACTION.select && !!state.lltCode && !!state.version && !state.term;
  useEffect(() => {
    if (!lookup) return;
    let cancelled = false;
    dictionaryTerm(state.lltCode!, state.version!, state.language ?? 'en')
      .then(term => {
        if (!cancelled) setResolved({ value, term, error: term.paths?.some(path => path.pathId === state.pathId) ? '' : 'The selected hierarchy path is unavailable. Choose a dictionary term again.' });
      })
      .catch(error => { if (!cancelled) setResolved({ value, error: `Coding preview could not be loaded: ${String(error)}` }); });
    return () => { cancelled = true; };
  }, [value, lookup, state.lltCode, state.version, state.language, state.pathId]);
  // Never flash an earlier command's term while a newer lookup is in flight or after Undo.
  const current = resolved?.value === value ? resolved : undefined;
  const term = state.term ?? current?.term;
  return { term, values: fieldId ? codingDraftValues(fieldId, state, term) : undefined,
    loading: lookup && !current, error: parsed.error || current?.error || '' };
}
